import { useCallback, useEffect, useState } from 'react';
import { Linking, Platform } from 'react-native';
import { useTheme } from '@/src/context/ThemeContext';
import { useAuth } from '@/src/context/AuthContext';
import { SUPPORTED_LANGUAGES, useLanguage, useTranslation } from '@/src/i18n';
import { SettingsScreen } from '@/src/presentation/screens/SettingsScreen';
import { NotificationType } from '@/src/domain/models/Notification';
import { APP_VERSION } from '@/src/core/config/appVersion';
import {
  getNotificationPreferencesUseCase,
  updateNotificationPreferenceUseCase,
  deleteAccountUseCase,
} from '@/src/core/dependencies';
import { logger } from '@/src/core/utils';
import { platformAlert } from '@/src/core/utils/platformAlert';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { NOTIFICATIONS_PERMISSION_KEY } from '@/src/core/utils/storageKeys';

const ALL_NOTIFICATION_TYPES: NotificationType[] = [
  'tournament_started',
  'tournament_invitation',
  'friend_request',
  'challenge_received',
  'match_result',
  'tournament_completed',
  'league_invitation',
];

export default function SettingsContainer() {
  const { colors, isDark, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState<Record<NotificationType, boolean>>(() => {
    const defaults: Record<string, boolean> = {};
    ALL_NOTIFICATION_TYPES.forEach((type) => { defaults[type] = true; });
    return defaults as Record<NotificationType, boolean>;
  });
  const [pushPermissionStatus, setPushPermissionStatus] = useState<'granted' | 'denied' | 'undetermined'>('undetermined');

  const loadPreferences = useCallback(async () => {
    if (!user) return;
    try {
      const prefs = await getNotificationPreferencesUseCase.execute(user.id);
      const map: Record<string, boolean> = {};
      ALL_NOTIFICATION_TYPES.forEach((type) => { map[type] = true; });
      prefs.forEach((p) => { map[p.notificationType] = p.enabled; });
      setNotificationPreferences(map as Record<NotificationType, boolean>);
    } catch (error) {
      logger.error('Settings', 'Failed to load notification preferences', { error });
    }
  }, [user]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const checkPushPermission = useCallback(async () => {
    if (Platform.OS === 'web') return;
    try {
      const { status } = await Notifications.getPermissionsAsync();
      setPushPermissionStatus(status as 'granted' | 'denied' | 'undetermined');
    } catch (error) {
      logger.warn('Settings', 'Failed to check push permission status', { error });
    }
  }, []);

  useEffect(() => {
    checkPushPermission();
  }, [checkPushPermission]);

  const handleEnablePushNotifications = async () => {
    try {
      await AsyncStorage.setItem(NOTIFICATIONS_PERMISSION_KEY, 'true');
      const { status } = await Notifications.requestPermissionsAsync();
      setPushPermissionStatus(status as 'granted' | 'denied' | 'undetermined');
    } catch (error) {
      logger.warn('Settings', 'Failed to enable push notifications', { error });
    }
  };

  const handleOpenSystemSettings = () => {
    Linking.openSettings().catch((error) => {
      logger.warn('Settings', 'Failed to open system settings', { error });
    });
  };

  const handleTogglePreference = async (type: NotificationType, enabled: boolean) => {
    if (!user) return;
    setNotificationPreferences((prev) => ({ ...prev, [type]: enabled }));
    try {
      await updateNotificationPreferenceUseCase.execute(user.id, type, enabled);
    } catch (error) {
      logger.error('Settings', 'Failed to update preference', { error });
      setNotificationPreferences((prev) => ({ ...prev, [type]: !enabled }));
    }
  };

  const handleDeleteAccount = () => {
    platformAlert(
      t('settings.deleteAccount'),
      t('settings.deleteAccountWarning'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('settings.deleteAccountConfirm'),
          style: 'destructive',
          onPress: async () => {
            if (!user) return;
            setDeletingAccount(true);
            try {
              const avatarUrl = user.user_metadata?.avatar_url as string | undefined;
              await deleteAccountUseCase.execute(user.id, avatarUrl);
              await signOut();
            } catch (error) {
              logger.error('Settings', 'Failed to delete account', { error });
              platformAlert(t('common.error'), t('errors.deleteAccountFailed'));
            } finally {
              setDeletingAccount(false);
            }
          },
        },
      ]
    );
  };

  return (
    <SettingsScreen
      isDark={isDark}
      toggleTheme={toggleTheme}
      language={language}
      setLanguage={setLanguage}
      supportedLanguages={SUPPORTED_LANGUAGES}
      colors={colors}
      notificationPreferences={notificationPreferences}
      onToggleNotificationPreference={handleTogglePreference}
      onDeleteAccount={handleDeleteAccount}
      deletingAccount={deletingAccount}
      pushPermissionStatus={pushPermissionStatus}
      onEnablePushNotifications={handleEnablePushNotifications}
      onOpenSystemSettings={handleOpenSystemSettings}
      appVersion={APP_VERSION}
    />
  );
}
